import Card from '@mui/material/Card';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useCallback, useMemo, useState } from 'react';
import { SearchInput } from '../search-input';
import { type IconPickerType, iconList } from './icons';

export interface IconPickerDropdownProps {
  value: IconPickerType | undefined;
  onChange: (value: IconPickerType | null) => void;
  onClose: () => void;
}

export function IconPickerDropdown({
  value,
  onChange,
  onClose,
}: IconPickerDropdownProps) {
  const [searchValue, setSearchValue] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const filteredIcons = useMemo(() => {
    return Object.entries(iconList).filter(([key, { search }]) => {
      const lowercaseQuery = searchValue.toLowerCase();
      return (
        key.toLowerCase().includes(lowercaseQuery) ||
        search.some((term) => term.toLowerCase().includes(lowercaseQuery))
      );
    });
  }, [searchValue]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const numColumns = 4;
      const numRows = Math.ceil(filteredIcons.length / numColumns);

      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          setSelectedIndex((prev) =>
            prev < filteredIcons.length - 1 ? prev + 1 : prev,
          );
          break;
        case 'ArrowLeft':
          event.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => {
            const currentRow = Math.floor(prev / numColumns);
            const currentCol = prev % numColumns;
            const newRow = currentRow > 0 ? currentRow - 1 : currentRow;
            const newIndex = newRow * numColumns + currentCol;
            return newIndex >= 0 ? newIndex : prev;
          });
          break;
        case 'ArrowDown':
          setSelectedIndex((prev) => {
            if (prev < 0) {
              return 0;
            }
            const currentRow = Math.floor(prev / numColumns);
            const currentCol = prev % numColumns;
            const newRow =
              currentRow < numRows - 1 ? currentRow + 1 : currentRow;
            const newIndex = newRow * numColumns + currentCol;
            return newIndex < filteredIcons.length ? newIndex : prev;
          });
          break;
        case 'Enter':
          if (selectedIndex >= 0) {
            const [key] = filteredIcons[selectedIndex];
            onChange(key as IconPickerType);
            onClose();
          }
          break;
        case 'Escape':
          onClose();
          break;
      }
    },
    [filteredIcons, selectedIndex, onChange, onClose],
  );

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Card
        sx={{
          width: 242,
          maxHeight: '80vh',
          overflow: 'auto',
          p: 1.5,
          pt: 0,
        }}
      >
        <SearchInput
          autoFocus
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          containerProps={{
            sx: {
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              pt: 1.5,
            },
          }}
        />
        <Grid container spacing={1} mt={0.25}>
          {filteredIcons.map(([key, { icon: Icon }], index) => (
            <Grid size={{ xs: 3 }} key={key}>
              <IconButton
                aria-label={key}
                onClick={() => {
                  onChange(value === key ? null : (key as IconPickerType));
                  onClose();
                }}
                sx={{
                  width: '100%',
                  height: 48,
                  backgroundColor:
                    selectedIndex === index || key === value
                      ? 'action.selected'
                      : 'transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Icon />
              </IconButton>
            </Grid>
          ))}
        </Grid>
      </Card>
    </ClickAwayListener>
  );
}
